import { ReactElement, useMemo, useState } from 'react'
import { useFormContext, useFormState } from 'react-hook-form'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import {
  Box,
  Button,
  Typography,
  Stack,
  InputAdornment,
  FormControlLabel,
  Switch,
  FormGroup,
  useTheme,
} from '@mui/material'
import type { ParticipantMemberDTO, ProjectFieldDTO } from '@api/fastAPI.schemas'
import {
  useGetOrganisationProjectFields,
  useGetProjectProjectFields,
} from '@api/organisation-project-fields/organisation-project-fields'
import {
  useGetOrganisationConstructionCompanies,
  useGetParticipantMembers,
  useGetProjectConstructionCompanies,
} from '@api/participants/participants'
import { useGetProjectMembers } from '@api/project-members/project-members'
import { useAuth0 } from '@auth0/auth0-react'
import { TextTabs } from '@ui2/navigation'
import { LoadingIndicator } from '@ui/feedback'
import { Form, TextField, Select } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { DraggablePopup } from '@ui/structure'
import CollapsibleBox from '@ui/structure/CollapsibleBox'
import useIsDevUser from 'src/hooks/useIsDevUser'
import { SHOW_CLICKUP_LINK } from '../constants'
import { createProjectSchema, baseProjectSchema } from '../schema'
import ActiveOrganisationField from './ActiveOrganisationField'
import AddConstructionCompanyForm from './AddConstructionCompanyForm'
import CustomProjectFields from './CustomProjectFields'
import { customFieldDefaults, withCustomFields } from './CustomProjectFields/utils'
import DescriptionField from './DescriptionField'
import FieldGroup from './FieldGroup'
import HandInProjectNameField from './HandInProjectNameField'
import ModelFileField from './ModelFileField'
import TagsField from './TagsField'
import { buildHandInProjectName } from './utils'

interface Props {
  id: string
  onSubmit: (project: ProjectData) => void
  project: ProjectData | null
}

type ProjectFormTab = 'basic' | 'extended'

const memberLabel = (member: ParticipantMemberDTO): string =>
  member.name || member.email || member.guid

/**
 * The start mode the dialog offers is a choice of *upload* -- an IFC file, or
 * a model JSON for developers -- while the backend contract also knows the
 * empty project. Uploading nothing is what creates one, so the payload's
 * start_mode is derived from what the user actually picked.
 */
const deriveModelStart = (values: {
  start_mode?: string
  ifc_file?: unknown
}): { start_mode: string; create_empty_model: boolean } => {
  if (values.start_mode === 'json') return { start_mode: 'json', create_empty_model: false }

  const hasIfcFile = !!values.ifc_file
  return { start_mode: hasIfcFile ? 'ifc' : 'empty', create_empty_model: !hasIfcFile }
}

const ProjectFormFields = ({
  project,
  customFields,
}: {
  project: ProjectData | null
  customFields: ProjectFieldDTO[]
}): ReactElement => {
  const { watch, setValue } = useFormContext()
  const { errors, submitCount } = useFormState()
  const { enqueueSnackbar } = useSnackbar()
  const theme = useTheme()
  // Starting from a model JSON is a developer tool, not a product feature.
  const { isDevUser } = useIsDevUser()
  const hasClickupData = watch('addClickupData')
  const startMode = watch('start_mode') || 'ifc'

  // With custom fields the form splits into tabs. A failed save shows the tab
  // holding the first error -- adjusted while rendering, on each new submit.
  const [tab, setTab] = useState<ProjectFormTab>('basic')
  const [handledSubmitCount, setHandledSubmitCount] = useState(submitCount)
  const hasExtendedErrors = !!errors.custom_field_values
  const hasBasicErrors = Object.keys(errors).some(key => key !== 'custom_field_values')
  if (submitCount !== handledSubmitCount) {
    setHandledSubmitCount(submitCount)
    if (hasBasicErrors) setTab('basic')
    else if (hasExtendedErrors) setTab('extended')
  }

  const [constructionCompanyAnchor, setConstructionCompanyAnchor] = useState<HTMLElement | null>(
    null,
  )

  const handleLoadError = (fallback: string) => (error: AxiosError) =>
    enqueueSnackbar(buildErrorMessage(error, fallback), { variant: 'error' })

  // The people pickers offer the organisation's members (R17): the project's
  // own organisation when editing, the active organisation when creating --
  // where no project exists yet to resolve one from.
  const { data: projectMembers } = useGetProjectMembers<ParticipantMemberDTO[], AxiosError>(
    project?.guid ?? '',
    {
      query: {
        enabled: !!project,
        select: data => data.members,
        onError: handleLoadError('Fehler beim Laden der Projektmitglieder'),
      },
    },
  )
  const { data: organisationMembers } = useGetParticipantMembers<
    ParticipantMemberDTO[],
    AxiosError
  >({
    query: {
      enabled: !project,
      select: data => data.members,
      onError: handleLoadError('Fehler beim Laden der Organisationsmitglieder'),
    },
  })
  const members = (project ? projectMembers : organisationMembers) || []

  // The Auftraggeberinnen follow the same split (R18): the project's own
  // organisation's list when editing, the active organisation's when creating.
  const { data: projectCompanies } = useGetProjectConstructionCompanies<string[], AxiosError>(
    project?.guid ?? '',
    {
      query: {
        enabled: !!project,
        select: data => data.companies.map(company => company.name),
        onError: handleLoadError('Fehler beim Laden der Auftraggeberinnen'),
      },
    },
  )
  const { data: organisationCompanies } = useGetOrganisationConstructionCompanies<
    string[],
    AxiosError
  >({
    query: {
      enabled: !project,
      select: data => data.companies.map(company => company.name),
      onError: handleLoadError('Fehler beim Laden der Auftraggeberinnen'),
    },
  })
  const constructionCompanies = project ? projectCompanies : organisationCompanies

  const nobody = { value: '', label: '—' }
  const responsibleOptions = [
    nobody,
    ...members
      .filter(member => member.is_responsible)
      .map(member => ({ value: member.guid, label: memberLabel(member) })),
  ]
  const editorOptions = [
    nobody,
    ...members.map(member => ({ value: member.guid, label: memberLabel(member) })),
  ]
  const constructionCompanyOptions = [
    nobody,
    ...(constructionCompanies || []).map(name => ({ value: name, label: name })),
  ]

  const handleClickupDataToggle = (isEnabled: boolean) => {
    setValue('addClickupData', isEnabled)
    if (!isEnabled) {
      // Clear Clickup info when disabling Clickup data
      setValue('clickup_info.clickup_project_no', '')
      setValue('clickup_info.clickup_project_name', '')
    }
  }

  // Developers only: switching the upload between IFC and a model JSON. The
  // file picked for the mode left behind would never be uploaded, so it goes.
  const handleStartModeChange = (useJson: boolean) => {
    setValue('start_mode', useJson ? 'json' : 'ifc')
    setValue('model', undefined)
    setValue('ifc_file', undefined)
  }

  const handleOpenConstructionCompanyPopover = (event: React.MouseEvent<HTMLElement>) => {
    setConstructionCompanyAnchor(event.currentTarget)
  }

  const handleCloseConstructionCompanyPopover = () => {
    setConstructionCompanyAnchor(null)
  }

  const basicData = (
    <Stack direction="column" spacing={2}>
      <Stack direction="row" spacing={3}>
        <FieldGroup title="Projekt">
          {/* The Clickup number, surfaced on its own while the Clickup box is
              hidden -- same BV- prefix, but optional. */}
          <TextField
            label="Projektnummer"
            name="clickup_info.clickup_project_no"
            data-cy="input-project-number"
            placeholder="1234"
            InputProps={{
              startAdornment: <InputAdornment position="start">BV-</InputAdornment>,
            }}
          />
          <TextField
            label="Projekt Name"
            name="name"
            data-cy="input-project-name"
            placeholder=" Müller..."
          />
          <DescriptionField />
          <HandInProjectNameField />
        </FieldGroup>
        <FieldGroup title="Bauvorhaben">
          <TextField
            label="Straße"
            name="address.street"
            data-cy="input-address-street"
            placeholder="Straße"
          />
          <Stack direction="row" spacing={1}>
            <Box sx={{ flex: '0 0 35%' }}>
              <TextField
                label="PLZ"
                name="address.zip_code"
                data-cy="input-address-zip"
                placeholder="PLZ"
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                label="Stadt"
                name="address.city"
                data-cy="input-address-city"
                placeholder="Stadt"
              />
            </Box>
          </Stack>
          <TextField label="Bauverantwortliche" name="owner_name" data-cy="input-owner-name" />
          <TextField
            label="Adresse des Bauverantwortlichen"
            name="owner_address"
            data-cy="input-owner-address"
            multiline={true}
            rows={3}
          />
        </FieldGroup>
        <FieldGroup title="Beteiligte">
          {/* Only when creating: an existing project keeps the organisation it
              was created in, which is not necessarily the active one. */}
          {!project && <ActiveOrganisationField />}
          <Select
            label="Planungsverantwortung"
            name="responsible_user_guid"
            placeholder="Planungsverantwortung auswählen"
            options={responsibleOptions}
            data-cy="input-responsible-engineer"
          />
          <Select
            label="Bearbeitung"
            name="primary_editor_guid"
            placeholder="Bearbeitung auswählen"
            options={editorOptions}
            // Bearbeitung is the Primärbearbeiter. Changing it later is
            // reassignment and admin-only (R12), done from the project list.
            disabled={!!project}
            tooltip={
              project
                ? {
                    text: 'Der Bearbeiter wird über die Projektliste geändert (nur Organisations-Admins).',
                  }
                : undefined
            }
            data-cy="input-processor-name"
          />
          <Select
            label="Auftraggeberin"
            name="construction_company_name"
            placeholder="Auftraggeberin auswählen"
            options={constructionCompanyOptions}
            data-cy="input-construction-company-name"
          />
          <Button
            variant="outlined"
            size="small"
            onClick={handleOpenConstructionCompanyPopover}
            data-cy="add-construction-company-button"
          >
            Neue Auftraggeberin hinzufügen
          </Button>
          {/* Tags stay editable for the project's whole life, so unlike the
              model row below they render when editing too. */}
          <TagsField />
        </FieldGroup>
      </Stack>
      {SHOW_CLICKUP_LINK && (
        <FormGroup>
          <CollapsibleBox
            variant="switch"
            label="Mit Clickup verknüpfen"
            isExpanded={hasClickupData}
            handleToggle={event => handleClickupDataToggle(event.target.checked)}
            dataCy="clickup-data"
          >
            <TextField
              label="Clickup Projekt Nummer"
              name="clickup_info.clickup_project_no"
              data-cy="input-clickup-project-no"
              placeholder="XXXX"
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">BV-</InputAdornment>,
              }}
            />
            <TextField
              label="Clickup Projekt Name"
              name="clickup_info.clickup_project_name"
              data-cy="input-clickup-project-name"
              placeholder="Karlsruher Str. (ohne BV-)"
              required
            />
          </CollapsibleBox>
        </FormGroup>
      )}
      {!project && (
        <Stack direction="row" spacing={2} justifyContent="space-between">
          {/* Nothing to head but the developer switch -- without it the
              upload field speaks for itself. */}
          {isDevUser && (
            <Stack direction="column" spacing={1} sx={{ width: '50%' }}>
              <Typography variant="body1" fontWeight="bold">
                Modell
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={startMode === 'json'}
                    onChange={event => handleStartModeChange(event.target.checked)}
                  />
                }
                label="Mit JSON starten"
              />
            </Stack>
          )}
          {/* Takes whatever the developer switch leaves over: half the row
              next to it, the full width without it. */}
          <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
            {startMode === 'json' ? (
              <ModelFileField name="model" label="Modell JSON" accept=".json,application/json" />
            ) : (
              <ModelFileField name="ifc_file" label="IFC-Datei (optional)" accept=".ifc" />
            )}
          </Stack>
        </Stack>
      )}
    </Stack>
  )

  return (
    <>
      {customFields.length ? (
        <Stack direction="column" spacing={2}>
          <TextTabs<ProjectFormTab>
            value={tab}
            onChange={setTab}
            showLabels
            scrollable
            items={[
              {
                id: 'basic',
                label: 'Grunddaten',
                color: hasBasicErrors ? theme.palette.error.main : undefined,
              },
              {
                id: 'extended',
                label: 'Erweiterte Daten',
                color: hasExtendedErrors ? theme.palette.error.main : undefined,
              },
            ]}
          />
          {/* Both tabs stay mounted in one grid cell: the fields keep their
              registration and the dialog keeps the height of the taller tab. */}
          <Box display="grid">
            <Box sx={{ gridArea: '1 / 1', visibility: tab === 'basic' ? 'visible' : 'hidden' }}>
              {basicData}
            </Box>
            <Box sx={{ gridArea: '1 / 1', visibility: tab === 'extended' ? 'visible' : 'hidden' }}>
              <CustomProjectFields fields={customFields} />
            </Box>
          </Box>
        </Stack>
      ) : (
        basicData
      )}

      <DraggablePopup
        title="Neue Auftraggeberin hinzufügen"
        anchorEl={constructionCompanyAnchor || undefined}
        closePopover={handleCloseConstructionCompanyPopover}
        zIndex={1500}
        paperSx={{
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        <AddConstructionCompanyForm
          onClose={handleCloseConstructionCompanyPopover}
          projectGuid={project?.guid}
        />
      </DraggablePopup>
    </>
  )
}

const ProjectDataForm = ({ id, onSubmit, project }: Props): ReactElement => {
  const { user } = useAuth0()
  const { enqueueSnackbar } = useSnackbar()

  // The organisation's own project data fields: the project's own organisation when editing, the
  // active one when creating (like the people pickers below).
  const handleFieldsError = (error: AxiosError) =>
    enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Projektdatenfelder'), {
      variant: 'error',
    })
  const { data: projectFields, isLoading: isLoadingProjectFields } = useGetProjectProjectFields<
    ProjectFieldDTO[],
    AxiosError
  >(project?.guid ?? '', { query: { enabled: !!project, onError: handleFieldsError } })
  const { data: organisationFields, isLoading: isLoadingOrganisationFields } =
    useGetOrganisationProjectFields<ProjectFieldDTO[], AxiosError>({
      query: { enabled: !project, onError: handleFieldsError },
    })
  const customFields = useMemo(
    () => (project ? projectFields : organisationFields) ?? [],
    [project, projectFields, organisationFields],
  )
  const validationSchema = useMemo(
    () => withCustomFields(project ? baseProjectSchema : createProjectSchema, customFields),
    [project, customFields],
  )

  // The form reads its schema and defaults once, at mount -- so it waits for the definitions.
  if (project ? isLoadingProjectFields : isLoadingOrganisationFields) return <LoadingIndicator />

  return (
    <Form
      id={id}
      validationSchema={validationSchema}
      defaultValues={
        project
          ? {
              ...baseProjectSchema.getDefault(),
              ...project,
              custom_field_values: customFieldDefaults(customFields, project.custom_field_values),
              responsible_user_guid: project.responsible_user_guid ?? '',
              primary_editor_guid: project.primary_editor_guid ?? '',
              addClickupData: project.project_type === 'customer_project',
              // Projects predating the composed name carry their own, so the
              // backend leaves the flag false for them.
              generate_hand_in_project_name: project.generate_hand_in_project_name ?? false,
            }
          : {
              ...createProjectSchema.getDefault(),
              // The creator works on their own project unless they hand it
              // to a colleague right here.
              primary_editor_guid: user?.sub ?? '',
              create_empty_model: false,
              start_mode: 'ifc',
              generate_hand_in_project_name: true,
              tags: [],
              custom_field_values: customFieldDefaults(customFields),
              storey_heights: [2.95],
            }
      }
      onSubmit={values => {
        const { addClickupData, ...projectValues } = values
        onSubmit({
          ...projectValues,
          // In automatic mode the name is composed on every save, so it
          // stays in step with the description and the name it is built from.
          // Typed names are left exactly as the user wrote them.
          ...(projectValues.generate_hand_in_project_name
            ? {
                hand_in_project_name: buildHandInProjectName(
                  projectValues.description,
                  projectValues.name,
                ),
              }
            : {}),
          // Editing never touches the model, so the start mode is a creation
          // concern only.
          ...(project ? {} : deriveModelStart(projectValues)),
          project_type: addClickupData ? 'customer_project' : 'test_project',
        } as ProjectData)
      }}
    >
      <ProjectFormFields project={project} customFields={customFields} />
    </Form>
  )
}

export default ProjectDataForm
